/* eslint-disable no-useless-escape */
import * as yup from 'yup'
import useTranslation from 'next-translate/useTranslation'
import { yupToFormErrors } from 'formik'
import { isPossiblePhoneNumber } from 'react-phone-number-input'
import { val } from 'dom7'

const useValidation = () => {
  const { t } = useTranslation()

  const validate = (data: Record<string, any>, rules: Record<string, any>) => {
    const newData = {}
    if (data) {
      Object.keys(data).forEach((key) => {
        let value = data[key]
        if (value === '') {
          value = null
        }
        if (value === null) {
          value = undefined
        }
        newData[key] = value
      })
    }
    const schema = yup.object().shape(rules)
    try {
      schema.validateSync(newData, { abortEarly: false })
      return { validated: true, errors: {} }
    } catch (err) {
      if (err.name === 'ValidationError') {
        const errors = yupToFormErrors(err)
        const response : {validated: boolean, errors?: Record<string, any>} = { validated: Object.keys(errors).length === 0 }
        if (!response.validated) {
          response.errors = errors
        }
        return response
      }
    }
    return { validated: true, errors: {} }
  }

  const requiredField = (type = yup.string) => type().required(t('validation:required')).min(1, t('validation:required'))
  const requiredFieldAny = (type = yup.mixed) => type().nullable().required(t('validation:required'))
  const requiredFieldBoolean = (type = yup.bool) => type().nullable().required(t('validation:required'))
  const requiredFieldObject = (type = yup.object) => type().required(t('validation:required'))
  const phoneField = () => requiredField().test({
    name: 'max',
    message: t('validation:phone_is_not_valid'),
    test: (value) => isPossiblePhoneNumber(value)
  })
  const emailField = () => requiredField()
    .matches(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/, t('validation:email'))
  const urlField = () => yup.string().nullable().url(t('validation:url'))
  const numberMin = (min: number) => requiredField(yup.number as never).min(min, t('validation:numberMin', { min: min.toString() }))
  const numberMax = (max: number) => requiredField(yup.number as never).max(max, t('validate:numberMax', { max: max.toString() }))
  const nullableField = (type = yup.string) => type().nullable()
  const requiredFieldEmphasizeField = (type = yup.string) => type().required()

  return {
    validate,
    requiredField,
    requiredFieldAny,
    requiredFieldBoolean,
    requiredFieldObject,
    emailField,
    urlField,
    numberMax,
    numberMin,
    nullableField,
    requiredFieldEmphasizeField,
    phoneField
  }
}

export default useValidation
